import { accountModule } from '@app/store'
import Response from '@app/types/src/schema/Response'
import { compress } from '@app/utils/image'
import firebase from 'firebase'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { $axios } from '@app/utils/api'
import { getExtentionName, uploadStorage } from '@app/utils/image'
import { getUUID } from '@app/utils/uuid'
import AccountType from '@app/constants/AccountType'
import SystemConstants from '@app/constants/SystemConstants'
import {
  DeviceIndivisual,
  DeviceRegisterResponseData,
  DeviceSalon,
} from '@app/types/src/device'
import { DeviceAuthorizeStatusType } from '@app/../types/src/device'
import * as Sentry from '@sentry/browser'

/**
 * レシート画像を圧縮し、construct-params API でファイルパスを取得する。
 * 成功時は files パスを返し、失敗時は reject 用の値を throw する。
 */
async function compressAndConstructParams(
  receipt: File
): Promise<any> {
  let file: File | Blob = receipt
  if (receipt.type.indexOf('image') > -1) {
    file = (await compress(receipt)) as File | Blob
  }

  const formData = new FormData()
  formData.set('file', file)

  const res = await $axios
    .post(
      `${process.env.BASE_PATH_V2}/v1/device/construct-params`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    .catch((e) => {
      console.log(e)
      return undefined
    })

  if (!res) {
    throw undefined
  }

  return res.data.data.files
}

@Module({
  name: 'device',
  stateFactory: true,
  namespaced: true,
})
export default class DeviceModule extends VuexModule {
  deviceRegisterId: string = ''
  deviceName: string = ''
  productSerialNumber: string = ''
  purchaseStore: string = ''
  purchaseUrl: string = ''
  purchaseDate: string = ''
  remarks: string = ''
  receipt: File | null = null
  //データの登録参照の時に使用するreceiptのURL
  receiptStorageUrl: string = ''
  //表示する際に使うreceiptのURL
  displayReceiptImageUrl: string = ''
  // 移行デバイスデータの領収書画像。移行データはGoogleDriveに画像が保存されている
  importReceiptFileUrl: string = ''
  branch: string = ''
  branchStaff: string = ''
  isInput: boolean = false
  deviceList: DeviceIndivisual[] | DeviceSalon[] = []
  deviceType: string = ''
  authorized: DeviceAuthorizeStatusType = 0
  warrantyPeriod: string = ''
  fromRepair: boolean = false //修理依頼導線から来たかどうか
  country: string = ''
  paymentId: string = ''
  @Mutation
  clearAll() {
    this.deviceRegisterId = ''
    this.deviceName = ''
    this.productSerialNumber = ''
    this.purchaseStore = ''
    this.purchaseUrl = ''
    this.purchaseDate = ''
    this.receipt = null
    this.receiptStorageUrl = ''
    this.displayReceiptImageUrl = ''
    this.importReceiptFileUrl = ''
    this.branch = ''
    this.branchStaff = ''
    this.isInput = false
    this.deviceList = []
    this.deviceType = ''
    this.authorized = 0
    this.warrantyPeriod = ''
    this.fromRepair = false
    this.fileBlobUrl = ''
    this.remarks = ''
    this.country = ''
    this.paymentId = ''
    localStorage.removeItem(SystemConstants.deviceIdKey)
  }
  @Mutation
  setFromRepair(value: boolean) {
    this.fromRepair = value
  }
  @Mutation
  setDeviceRegisterId(value: string) {
    this.deviceRegisterId = value
  }

  @Mutation
  setDeviceName(value: string) {
    this.deviceName = value
  }

  @Mutation
  setProductSerialNumber(value: string) {
    this.productSerialNumber = value
  }

  @Mutation
  setPurchaseStore(value: string) {
    this.purchaseStore = value
  }

  @Mutation
  setPurchaseUrl(value: string) {
    this.purchaseUrl = value
  }

  @Mutation
  setPurchaseDate(value: string) {
    this.purchaseDate = value
  }

  @Mutation
  setReceipt(value: File | null) {
    this.receipt = value
  }

  @Mutation
  setReceiptStorageUrl(value: string) {
    this.receiptStorageUrl = value
  }

  @Mutation
  setDisplayReceiptImageUrl(value: string) {
    this.displayReceiptImageUrl = value
  }

  @Mutation
  setBranch(value: string) {
    this.branch = value
  }

  @Mutation
  setBranchStaff(value: string) {
    this.branchStaff = value
  }

  @Mutation
  setIsInput(value: boolean) {
    this.isInput = value
  }

  @Mutation
  setDeviceList(value: DeviceIndivisual[] | DeviceSalon[]) {
    this.deviceList = value
  }

  @Mutation
  setDeviceType(value: string) {
    this.deviceType = value
  }

  @Mutation
  setAuthorized(value: DeviceAuthorizeStatusType) {
    this.authorized = value
  }

  @Mutation
  setWarrantyPeriod(value: string) {
    this.warrantyPeriod = value
  }

  fileBlobUrl: string = ''
  @Mutation
  setFileBlobUrl(value: string) {
    this.fileBlobUrl = value
  }
  @Mutation
  setImportReceiptFileUrl(value: string) {
    this.importReceiptFileUrl = value
  }
  @Mutation
  setRemarks(value: string) {
    this.remarks = value
  }
  @Mutation
  setCountry(value: string) {
    this.country = value
  }
  @Mutation
  setPaymentId(value: string) {
    this.paymentId = value
  }
  @Action({ rawError: true })
  public createDevice() {
    return new Promise<Response<DeviceRegisterResponseData>>(
      async (resolve, reject) => {
        let files: any
        try {
          files = await compressAndConstructParams(this.receipt!)
        } catch (e) {
          reject(e)
          return
        }

        let request: DeviceIndivisual | DeviceSalon
        if (accountModule.accountType == AccountType.indivisual) {
          request = {
            product_serial_number: this.productSerialNumber,
            purchase_store: this.purchaseStore,
            purchase_date: this.purchaseDate,
            purchase_url: this.purchaseUrl,
            receipt: this.receiptStorageUrl,
            device_type: this.deviceType,
            remarks: this.remarks,
            file: files,
            country: this.country,
          }
        } else {
          request = {
            product_serial_number: this.productSerialNumber,
            purchase_store: this.purchaseStore,
            purchase_date: this.purchaseDate,
            branch: this.branch,
            branch_staff: this.branchStaff,
            receipt: this.receiptStorageUrl,
            device_type: this.deviceType,
            remarks: this.remarks,
            file: files,
            country: this.country,
          }
        }

        await $axios
          .post(`${process.env.BASE_PATH_V2}/v1/device`, request)
          .then((result) => {
            console.log(result)
            localStorage.removeItem(SystemConstants.deviceIdKey)
            resolve(result.data as Response<DeviceRegisterResponseData>)
          })
          .catch((e) => {
            reject(e)
          })
      }
    )
  }
  @Action({ rawError: true })
  public updateDeviceImage() {
    return new Promise<Response<DeviceRegisterResponseData>>(
      async (resolve, reject) => {
        let files: any
        try {
          files = await compressAndConstructParams(this.receipt!)
        } catch (e) {
          reject(e)
          return
        }

        const request = {
          id: this.deviceRegisterId,
          file: files,
        }

        await $axios
          .put(`${process.env.BASE_PATH_V2}/v1/device/image`, request)
          .then((result) => {
            resolve(result.data as Response<DeviceRegisterResponseData>)
          })
          .catch((e) => {
            reject(e)
          })
      }
    )
  }
  @Action({ rawError: true })
  public updateDevice() {
    // Sentry に使用ログを残す（例：accountType と deviceRegisterId）
    try {
      Sentry.addBreadcrumb({
        category: 'store.device',
        message: 'updateDevice called',
        level: 'info',
        data: {
          accountType: accountModule.accountType,
          deviceRegisterId: this.deviceRegisterId,
        },
      })
      Sentry.captureMessage('store.device.updateDevice invoked', 'info')
    } catch (e) {
      console.warn('Sentry logging failed', e)
    }

    return new Promise<Response<null>>((resolve, reject) => {
      let request: any
      if (accountModule.accountType == AccountType.indivisual) {
        request = {
          device_register_id: this.deviceRegisterId,
          purchase_store: this.purchaseStore,
          purchase_date: this.purchaseDate,
          purchase_url: this.purchaseUrl,
          receipt: this.receiptStorageUrl,
        }
      } else {
        request = {
          device_register_id: this.deviceRegisterId,
          purchase_store: this.purchaseStore,
          purchase_date: this.purchaseDate,
          branch: this.branch,
          branch_staff: this.branchStaff,
          receipt: this.receiptStorageUrl,
        }
      }

      $axios
        .put(`${process.env.BASE_PATH}/device`, request)
        .then((result: Response<null>) => {
          localStorage.setItem(SystemConstants.deviceIdKey, '')
          resolve(result)
        })
        .catch((e) => {
          reject(e)
        })
    })
  }
  @Action({ rawError: true })
  public getDevice(id: string) {
    return new Promise<void>((resolve, reject) => {
      $axios
        .get(`${process.env.BASE_PATH_V2}/v1/device?id=${id}`)
        .then(async (result) => {
          this.setProductSerialNumber(result.data.data.product_serial_number)
          this.setDeviceRegisterId(result.data.data.device_register_id)
          this.setPurchaseDate(result.data.data.purchase_date)
          this.setPurchaseStore(result.data.data.purchase_store)
          this.setPurchaseUrl(result.data.data.purchase_url)
          this.setBranch(result.data.data.branch)
          this.setBranchStaff(result.data.data.branch_staff)
          this.setAuthorized(result.data.data.authorized)
          this.setDeviceType(result.data.data.device_type)
          this.setReceiptStorageUrl(result.data.data.receipt)
          this.setWarrantyPeriod(result.data.data.warranty_period)
          this.setImportReceiptFileUrl(result.data.import_receipt_file)
          this.setRemarks(result.data.data.remarks)
          this.setCountry(result.data.data.country)
          this.setPaymentId(result.data.data.payment_id)
          if (result.data.data.file_obj) {
            const arrayBufferView = new Uint8Array(
              result.data.data.file_obj.buffer.data
            )
            const blob = new Blob([arrayBufferView], {
              type: result.data.data.file_obj.contentType,
            })
            const windowUrl = window.URL || window.webkitURL
            const blobUrl = windowUrl.createObjectURL(blob)
            this.setFileBlobUrl(blobUrl)
            await this.getStorageUrl(result.data.data.receipt)
              .then((result) => {
                this.setDisplayReceiptImageUrl(result)
              })
              .catch((e) => {})

            resolve()
          } else {
            resolve()
          }
        })
        .catch((e) => {
          reject(e)
        })
    })
  }

  @Action({ rawError: true })
  public getDevices() {
    return new Promise<Response<any>>((resolve, reject) => {
      $axios
        .get(`${process.env.BASE_PATH_V2}/v1/user-devices`)
        .then(async (result) => {
          // backendからアカウントタイプに応じたデバイスが返ってくる
          this.setDeviceList(result.data.data)
          resolve(result.data)
        })
        .catch((e) => {
          reject(e)
        })
    })
  }

  @Action({ rawError: true })
  //pathに指定している場合はそちらを優先する
  public uploadStorage(path?: string) {
    const ext = getExtentionName(this.receipt!.name)
    if (path) {
      //拡張子は変更される可能性があるので設定したものを使用する
      this.setReceiptStorageUrl(`${path.split('.')[0]}.${ext}`)
    } else {
      this.setReceiptStorageUrl(`receipt/device/${getUUID()}.${ext}`)
    }

    return uploadStorage(this.receipt!, this.receiptStorageUrl!)
  }
  @Action({ rawError: true })
  //pathに指定している場合はそちらを優先する
  public updateStorage(path: string) {
    const ext = getExtentionName(this.receipt!.name)
    if (ext) {
      //拡張子は変更される可能性があるので設定したものを使用する
      this.setReceiptStorageUrl(`${path.split('.')[0]}.${ext}`)
    } else {
      this.setReceiptStorageUrl(`receipt/device/${getUUID()}.${ext}`)
    }

    return uploadStorage(this.receipt!, this.receiptStorageUrl!)
  }

  @Action({ rawError: true })
  public getStorageUrl(path: string) {
    return new Promise<string>((resolve, reject) => {
      const storageRef = firebase.storage().ref()
      const downloadRef = storageRef.child(path)
      downloadRef
        .getDownloadURL()
        .then((url) => {
          resolve(url)
        })
        .catch((e) => {
          reject(e)
        })
    })
  }

  // 既に登録されているデバイスかどうか
  @Action({ rawError: true })
  public isExistDevice() {
    return new Promise<boolean>((resolve, reject) => {
      $axios
        .get(
          `${process.env.BASE_PATH_V2}/v1/device/exist?serial_number=${this.productSerialNumber}`
        )
        .then((result) => {
          resolve(result.data.data.is_exist)
        })
        .catch((e) => {
          reject(e)
        })
    })
  }

  // 存在するシリアルナンバーかどうかを確認
  @Action({ rawError: true })
  public isExistSerial(): any {
    return new Promise<boolean>((resolve, reject) => {
      $axios
        .get(
          `${process.env.BASE_PATH_V3}/device/exist/serial?serialNumber=${this.productSerialNumber}`
        )
        .then((result) => {
          this.setCountry(result.data.data.country)
          resolve(result.data.data)
        })
        .catch((e) => {
          reject(e)
        })
    })
  }
}
